import { Employee } from "@finway-group/shared/lib/models/user/employee.model"
import { Alert, Divider, Form, FormInstance, Select } from "antd"
import React, { useEffect, useMemo } from "react"
import { useTranslation } from "react-i18next"
import { UserImage } from "~Components/UserImage"
import { NOT_SET_VALUE } from "~Shared/config/consts"
import { useCompany } from "~Shared/hooks/company.hooks"
import { useEmployees } from "~Shared/hooks/employee.hooks"
import { UserService } from "~Shared/services"
import { doFilterSelect } from "~Shared/utils/helper.utils"
import { createSubstituteRules } from "./rules/substitute.rules"

const { Option } = Select

export interface SubstituteFormValues {
    primary_substitute: string
    secondary_substitute: string
}

interface SubstituteFormProps {
    formInstance: FormInstance<SubstituteFormValues>
    onSubmit: (values: SubstituteFormValues) => void
}

export const SubstituteForm = ({ onSubmit, formInstance }: SubstituteFormProps) => {
    const { t } = useTranslation()
    const rules = createSubstituteRules()
    const company = useCompany()
    const loggedInEmployee = UserService.getLoggedInEmployeeProfile()
    const employeeOptions = useEmployees({ excludeDeleted: true }).filter((employee) => employee.id !== loggedInEmployee.id)
    const approverList = UserService.getApproverEmployeeList(employeeOptions as Array<Employee>)
    const primarySubstitute = Form.useWatch(["primary_substitute"], formInstance)
    const secondarySubstitute = Form.useWatch(["secondary_substitute"], formInstance)

    useEffect(() => {
        if (primarySubstitute && primarySubstitute === secondarySubstitute) {
            formInstance.resetFields(["secondary_substitute"])
        }
    }, [primarySubstitute, secondarySubstitute, formInstance])

    const selectedPrimarySubstitute = useMemo(() => {
        const employee = approverList.find((e: Employee) => e.id === primarySubstitute)
        employee?.setActiveCompanyProfile(company._id)
        return employee
    }, [approverList, primarySubstitute])

    const selectedSecondarySubstitute = useMemo(() => {
        const employee = approverList.find((e: Employee) => e.id === secondarySubstitute)
        employee?.setActiveCompanyProfile(company._id)
        return employee
    }, [approverList, secondarySubstitute])

    return (
        <Form id="substituteForm" form={formInstance} layout="vertical" onFinish={onSubmit}>
            <Form.Item label={t("info:employee_absence.modal.primary_substitute")} name="primary_substitute" required rules={rules.primary_substitute}>
                <Select showSearch filterOption={doFilterSelect}>
                    {approverList.map((employee: Employee) => (
                        <Option key={employee.id} label={employee.getFullName()} value={employee.id}>
                            <div className="flex items-center">
                                <UserImage user={employee} />
                                {employee.getFullName()}
                            </div>
                        </Option>
                    ))}
                </Select>
            </Form.Item>
            {company?.cardsEnabled && !selectedPrimarySubstitute?.isEnrolledInCardProgramOfCompany({ companyId: company._id.toString() }) && (
                <NonCardProgramUserAlert userName={selectedPrimarySubstitute?.getFullName()} />
            )}
            <Divider style={{ margin: "20px 0" }} />
            <Form.Item label={t("info:employee_absence.modal.secondary_substitute")} name="secondary_substitute">
                <Select showSearch filterOption={doFilterSelect}>
                    {approverList
                        .filter((employee: Employee) => employee.id !== primarySubstitute)
                        .map((employee: Employee) => (
                            <Option key={employee.id} label={employee.getFullName()} value={employee.id}>
                                <div className="flex items-center">
                                    <UserImage user={employee} />
                                    {employee.getFullName()}
                                </div>
                            </Option>
                        ))}
                    <Option value={NOT_SET_VALUE} label="">
                        <div className="flex items-center">
                            <span>{t("info:employee_absence.modal.no_secondary_substitute_set")}</span>
                        </div>
                    </Option>
                </Select>
            </Form.Item>
            {company?.cardsEnabled && !selectedSecondarySubstitute?.isEnrolledInCardProgramOfCompany({ companyId: company._id.toString() }) && (
                <NonCardProgramUserAlert userName={selectedSecondarySubstitute?.getFullName()} />
            )}
        </Form>
    )
}

const NonCardProgramUserAlert = ({ userName }: { userName: string | undefined }) => {
    const { t } = useTranslation()
    if (!userName) {
        return null
    }
    return (
        <Alert
            banner
            type="warning"
            message={t("info:employee_absence.not_card_program_alert_banner:title", { userName })}
            description={t("info:employee_absence.not_card_program_alert_banner:message")}
        />
    )
}
